import { Injectable } from '@angular/core';
import { ICartGroup, ICartProduct } from '../models/cart.model';
import { Store } from '@ngrx/store';
import { cartAddProductAction, cartDeleteProductAction, cartUpdateProductAction } from '../store/cart.actions';
import { AppState } from '../app.reducer';
import { combineLatest, debounceTime, Observable, of, take } from 'rxjs';
import {
  cartCommentSelector,
  cartPaymentTypeIdSelector,
  cartProducts,
  cartPromoCodeSelector,
  cartTotal
} from '../store/cart.selectors';
import { CreateOrderModifier, CreateOrderRequest } from '../models/api.models';
import { switchMap } from 'rxjs/operators';
import { DeliveryType } from 'app/models/user-order.model';
import { currentAddressSelector, currentDeliveryZoneSelector } from 'app/store/state.selectors';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  constructor(
    private store: Store<AppState>,
  ) {
  }

  save(cartProduct: ICartProduct): void {
    // сначала отбрасываем группы, которые не заполнены модификаторами клиентом
    const selectedGroups = cartProduct.groups
      .filter(group => group.selectedQuantity)
      .map(group => {
        let selectedGroup = {...group, modifiers: group.modifiers.filter(modifier => modifier.selectedQuantity)};
        return selectedGroup;
      });
    cartProduct.groups = selectedGroups;

    this.store.dispatch(cartAddProductAction(cartProduct));
  }

  updateQuantity(product: ICartProduct, quantity: number): void {
    this.store.select(cartProducts)
      .pipe(take(1))
      .subscribe(products => {
        if (product.groups.length === 0) {
          const foundProduct = products.find((cartProduct) => cartProduct.id === product.id);
          if (foundProduct) {
            if (foundProduct.quantity + quantity > 0) {
              this.store.dispatch(cartUpdateProductAction({
                product: foundProduct,
                quantity
              }));
            } else {
              this.store.dispatch(cartDeleteProductAction(foundProduct));
            }
          } else if (quantity > 0) {
            this.save(product);
          }
        } else {
          this.save(product);
        }
      });
  }

  CreateOrderRequestAsync(): Observable<CreateOrderRequest | null> {
    return combineLatest([
      this.store.select(currentAddressSelector),
      this.store.select(currentDeliveryZoneSelector),
      this.store.select(cartCommentSelector),
      this.store.select(cartPromoCodeSelector),
      this.store.select(cartPaymentTypeIdSelector),
      this.store.select(cartProducts),
      this.store.select(cartTotal)
    ])
      .pipe(
        debounceTime(100),
        switchMap(([
                     currentAddress,
                     deliveryZone,
                     comment,
                     promoCode,
                     paymentType,
                     products,
                     cartTotal
                   ]) => {
          // currentAddress - перед добавлением товара в корзину мы обязательно определяем адрес клиента
          // собственно поэтому если адрес пустой, то это явно не требует расчётов
          let request: CreateOrderRequest | null = null;
          if (currentAddress) {
            request = {
              cartOrder: {
                // если доставка, то id это id адреса, иначе это id терминала
                addressId: currentAddress.type === DeliveryType.DELIVERY ? currentAddress.id : null,
                comment,
                deliveryTerminalId: currentAddress.deliveryTerminal.id,
                deliveryZoneId: deliveryZone ? deliveryZone.id : null,
                estimatedDeliveryDate: null,
                deliveryType: currentAddress.type,
                payment: {
                  charges: 0,
                  orderTotal: Number(cartTotal.price.toFixed(2)),
                  total: Number(cartTotal.price.toFixed(2)),
                  delivery: 0,
                  promoCode: promoCode,
                  terminalPaymentTypeId: paymentType,
                },
                orderDiscountInfo: null,
                products: products.map(product => {
                  return {
                    id: product.id,
                    positionId: product.positionId,
                    price: product.price,
                    sizeId: product.selectedSizeId === '' ? null : product.selectedSizeId,
                    modifiers: this.collectModifiers(product.groups),
                    quantity: product.quantity,
                  };
                })
              }
            }
          }

          return of(request);
        })
      );
  }

  private collectModifiers(groups: ICartGroup[]): CreateOrderModifier[]{
    let resultModifiers: CreateOrderModifier[] = [];
    groups.forEach(group => {
      const groupModifiers = group.modifiers.map(modifier => ({
        ...modifier,
        price: modifier.calculatedPrice,
        quantity: modifier.selectedQuantity,
      } as CreateOrderModifier));
      resultModifiers = [...resultModifiers, ...groupModifiers];
    });

    return resultModifiers;
  }
}
